// #492 F5: the Home program card's door (usePlanGapsLauncher). The cycle's MEV
// gaps still open this week become today's draft gaps; the program is never written.
import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import type { ModelFitGaps } from "./programModelFitGaps.js";
import type { TodayDraftRow, TodayGap } from "./todaySessionProposal.js";

/** What the athlete's day adds to the program's gaps. */
export type TodayFromFitDay = {
  /** Hard sets done this calendar week, by muscle, synergist credit included
   *  (D3). A muscle not in it did none. */
  weekSets: ReadonlyMap<CatalogMuscleSlug, number>;
  /** Last session that gave the muscle a hard set. Not in it: never. */
  lastTrainedMs?: ReadonlyMap<CatalogMuscleSlug, number>;
  /** The exercises today may use; a candidate outside is dropped. Absent:
   *  every one the fit recommended. */
  slugs?: ReadonlySet<string>;
};

/** One MEV gap per `belowMev` muscle the week has not closed yet and that
 *  keeps a candidate, in the fit's order (worst first). `gap` stays the
 *  cycle's; today's gap is MEV less `weekSets` (regapTodayRows). */
export const todayGapsFromFit = (
  fit: Pick<ModelFitGaps, "belowMev" | "recommendations">,
  day: TodayFromFitDay,
): TodayGap[] =>
  fit.belowMev.flatMap((g): TodayGap[] => {
    const mev = g.weeklySets + g.gap;
    const weekSets = day.weekSets.get(g.muscle) ?? 0;
    if (weekSets >= mev) return [];
    const rec = fit.recommendations.find((r) => r.muscle === g.muscle);
    const candidates = (rec?.candidates ?? [])
      .filter((c) => !day.slugs || day.slugs.has(c.slug))
      .map((c) => ({ slug: c.slug, setsToClose: c.setsToClose, weight: c.weight }));
    if (candidates.length === 0) return [];
    return [
      {
        muscle: g.muscle,
        gap: g.gap,
        targetSets: mev,
        weekSets,
        lastTrainedMs: day.lastTrainedMs?.get(g.muscle) ?? null,
        candidates,
      },
    ];
  });

/** The MEV muscles the minutes left out: a row with a gap still open and no
 *  sets after the fit (fitTodayRows). What the draft must say it dropped.
 *  Each muscle once, in the rows' order. */
export const todayOutOfTime = (rows: ReadonlyArray<TodayDraftRow>): CatalogMuscleSlug[] => [
  ...new Set(
    rows.filter((r) => r.target !== "mav" && r.gap > 0 && r.sets === 0).map((r) => r.muscle),
  ),
];
